import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import {
  SET_REPORT_FILTERS,
  UPDATE_REPORT_FILTER,
  RESET_REPORT_FILTERS
} from '../utils/Types';

/**
 * Custom hook for managing report filters with Redux
 * @param {string} reportType - The type of report (e.g., 'dailyInventoryDistribution')
 * @returns {Object} Filter state and handlers
 */
export function useReportFilters(reportType) {
  const dispatch = useDispatch();
  const filters = useSelector(state => state.reportFilters?.[reportType]);

  // Local state for immediate updates before dispatching
  const [localFilters, setLocalFilters] = useState(filters || {});

  // Update local filters when Redux state changes
  useEffect(() => {
    if (filters) {
      setLocalFilters(filters);
    }
  }, [filters]);

  /**
   * Update a single filter value
   * @param {string} key - Filter key to update
   * @param {*} value - New value for the filter
   */
  const updateFilter = (key, value) => {
    const updated = {
      ...localFilters,
      [key]: value
    };
    setLocalFilters(updated);
    dispatch({
      type: UPDATE_REPORT_FILTER,
      payload: { reportType, key, value }
    });
  };

  /**
   * Update multiple filters at once
   * @param {Object} newFilters - Object containing filter updates
   */
  const updateMultipleFilters = (newFilters) => {
    const updated = {
      ...localFilters,
      ...newFilters
    };
    setLocalFilters(updated);
    dispatch({
      type: SET_REPORT_FILTERS,
      payload: { reportType, filters: newFilters }
    });
  };

  /**
   * Reset filters to defaults
   */
  const reset = () => {
    dispatch({
      type: RESET_REPORT_FILTERS,
      payload: reportType
    });
  };

  return {
    filters: localFilters,
    updateFilter,
    updateMultipleFilters,
    reset
  };
}

export default useReportFilters;
